import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import API from "../../../service/api";
import { CustomModal } from "../../../components/CustomModal";
import { MDBBtn, MDBCheckbox, MDBIcon, MDBInput } from "mdb-react-ui-kit";
import { LoadingSpinner } from "../../../components/LoadingSpinner";
import { Alert } from "../../../components/Alert";
import { TextInput } from "../../../components/core/inputs/TextInput";

export interface ShareGroupModalProps {
    open: boolean;
    groupId: number;
    onClose: () => void;
    onConfirm: () => void;
}
export const ShareGroupModal = ({open, groupId, onClose, onConfirm}: ShareGroupModalProps) => {
    const [selectedId, setSelectedId] = useState<number|null>(null);
    const [selectedPermissionId, setSelectedPermissionId] = useState<number|null>(null);
    const [companies, setCompanies] = useState<any[]>([]);
    const [search, setSearch] = useState('');
    const [permissions, setPermissions] = useState<any[]>([]);
    const [shareWithLink, setShareWithLink] = useState(false);
    const [shareLoading, setShareLoading] = useState(false);
    const [shareError, setShareError] = useState('');
    const [shareInvitation, setShareInvitation] = useState(null);
    const [shareSuccessMessage, setShareSuccessMessage] = useState('');
    const { t } = useTranslation();

    useEffect(() => {
        if (!open) return;

        setSearch('');
        setSelectedId(null);
        setShareWithLink(false);
        setShareLoading(false);
        setShareSuccessMessage('');
        setShareError('');
        setShareInvitation(null);
        setCompanies([]);
        API.GET(`/api/admgroups/${groupId}/share`)
            .then(v => {
                let c: any[] = [];
                if (v.parent) c.push({isParent: true, ...v.parent});
                if (v.subcompanies) c = c.concat(v.subcompanies);
                setCompanies(c);
            })
            .catch(err => alert(err.message));
        API.GET('/api/rights')
            .then(r => setPermissions(r.rights))
            .catch(err => alert(err.message));
    }, [open]);

    const confirm = useCallback(() => {
        if (!shareWithLink){
            setShareLoading(true);
            API.POST(`/api/admgroups/${groupId}/share/${selectedId}`, { rightsId: selectedPermissionId })
                .then(() => { setShareSuccessMessage(t('group_share_success')); onConfirm(); })
                .catch(err => { setShareError(err.message); })
                .finally(() => setShareLoading(false));
        }else{
            setShareLoading(true);
            API.POST(`/api/admgroups/${groupId}/sharelink`, { rightsId: selectedPermissionId })
                .then(({invitationId}) => { setShareInvitation(invitationId); })
                .catch(err => { setShareError(err.message); })
                .finally(() => setShareLoading(false));
        }

    }, [groupId, selectedId, selectedPermissionId, shareWithLink]);

    return (
        <CustomModal
            open={open}
            onClose={onClose}
            title={t('share_group')}
            footer={<>
                <MDBBtn color="secondary" onClick={onClose}>{t('cancel')}</MDBBtn>
                { !(shareError || shareSuccessMessage || shareInvitation) && <MDBBtn color="primary" disabled={(!shareWithLink && !selectedId) || !selectedPermissionId} onClick={confirm}>{t('confirm')}</MDBBtn> }
            </>}
        >
            {(companies == null || shareLoading) && <LoadingSpinner/>}
            {companies !== null && (
                <div className="d-flex flex-column">
                    <Alert type="danger" message={shareError}/>
                    <Alert type="success" message={shareSuccessMessage}/>
                    {shareInvitation && (
                        <div className="d-flex flex-column align-items-center justify-content-center">
                            <span>{t('your_invitation_id')}:</span>
                            <h3 className="text-success" style={{letterSpacing: '10px', marginLeft: 10, fontWeight: 'bold'}}>{shareInvitation}</h3>
                        </div>
                    )}

                    <div className="d-flex flex-column w-100">
                        {(!shareError && !shareSuccessMessage && !shareInvitation) && <>
                            <TextInput label={t('search')} value={search} onValueChange={setSearch}/>
                            <MDBCheckbox label={t('share_with_invitation')} type="checkbox" checked={shareWithLink} id="shareWithLink" onChange={ev => setShareWithLink(ev.currentTarget.checked)}/>

                            <div className="d-flex flex-column overflow-auto px-1 pt-2 pb-2" style={{maxHeight: 500, minWidth: 300, userSelect: 'none'}}>
                                {companies.map(l => (
                                    <div
                                        key={l.id}
                                        className={"flex-row align-items-center hover-shadow p-1"+(shareWithLink?' d-none':'')}
                                        style={{cursor: 'pointer', background: l.id == selectedId ? '#0000001a' :'', display: l.name.toLocaleUpperCase().indexOf(search.toLocaleUpperCase()) == '-1' ? 'none': 'flex'}}
                                        onClick={() => setSelectedId(l.id)}
                                    >
                                        <MDBIcon icon="building" color="primary"/>
                                        <span className="ms-1">{l.name}</span>
                                        {l.isParent && <span className="fst-italic">&nbsp;({t('parent_lower')})</span>}
                                    </div>
                                ))}
                            </div>
                            <span className="w-100 text-center"><h6>{t('permissions')}</h6></span>

                            <div className="d-flex flex-column overflow-auto px-1 pt-2 pb-2" style={{maxHeight: 500, minWidth: 300, userSelect: 'none'}}>
                                {permissions.map(p => (
                                    <div
                                        key={p.id}
                                        className="flex-row align-items-center hover-shadow p-1"
                                        style={{cursor: 'pointer', background: p.id == selectedPermissionId ? '#0000001a' :'', display: p.Name.toLocaleUpperCase().indexOf(search.toLocaleUpperCase()) == '-1' ? 'none': 'flex'}}
                                        onClick={() => setSelectedPermissionId(p.id)}
                                    >
                                        <MDBIcon icon="briefcase" color="primary"/>
                                        <span className="ms-1">{p.Name}</span>
                                    </div>
                                ))}
                            </div>
                        </>}
                    </div>
                </div>
            )}
        </CustomModal>
    )
}